/**
 * Board style context. Lets the player pick from five preset CSS color schemes
 * for the board squares. Choice persists in localStorage as `chess_board_style`
 * so it carries across reloads in the same browser.
 *
 * Styles ship as `[data-board-style="<value>"]` blocks in App.css that override
 * the `--light-sq` / `--dark-sq` CSS variables; the GameBoard component reads
 * them via the existing .cell-light / .cell-dark classes.
 *
 * ---------------------------------------------------------------------------
 * Render mode (additive, opt-in per style — Feb 2026):
 * ---------------------------------------------------------------------------
 * Each entry may carry an optional `renderMode` field with one of two values:
 *
 *   "per-cell"    (default when unset) — the legacy pipeline. Each
 *                 `.cell-light` / `.cell-dark` div paints its own copy of the
 *                 artwork, cropped by `background-size: cover` to that square.
 *                 Every existing style uses this mode; nothing about it has
 *                 changed.
 *
 *   "full-board"  NEW. A single continuous image is painted on the
 *                 `.board-grid` (or `.board-grid-fpc`) container so the
 *                 artwork reads as one coherent picture across the whole
 *                 board. The checkerboard rhythm is layered ON TOP via a
 *                 repeating-conic-gradient overlay applied on the grid's
 *                 `::after`. Cells become transparent so the underlying
 *                 image + mask show through; pieces sit ABOVE the mask via
 *                 an explicit stacking-context bump on `.cell`.
 *                 Opt in per style by adding `renderMode: "full-board"` to
 *                 that style's entry AND providing `--board-full-image`
 *                 (and optionally `--board-mask-color`, `--board-mask-alpha`)
 *                 in the style's CSS block. No existing style is opted in
 *                 as part of this build — the capability is present but
 *                 unassigned until a future task chooses which artworks
 *                 to promote.
 *
 * The current style's render mode is written to `<html>` as
 * `data-board-render-mode="…"` alongside the existing `data-board-style="…"`
 * attribute. App.css guards every full-board rule under
 * `html[data-board-render-mode="full-board"]` so switching modes is a pure
 * attribute swap with zero JS churn.
 */
import { createContext, useContext, useEffect, useState } from "react";
import { SEAT_TO_FACTION, FACTION_THEMES } from "@/lib/factionThemes";

export const BOARD_STYLES = [
  { value: "wood",             label: "Classic Wood" },
  { value: "marble",           label: "Marble" },
  { value: "green-felt",       label: "Green Felt" },
  { value: "blue-stone",       label: "Blue Stone" },
  { value: "neon-whiteboard",  label: "Neon Whiteboard" },
  { value: "dragonborn-flame", label: "Dragonborn Flame" },
  { value: "elf-court-flame",  label: "Elf Court Flame" },
  { value: "celestial-host-flame", label: "Celestial Host Flame" },
  { value: "iron-forge",       label: "Iron Forge" },
  { value: "inkboard",         label: "Inkboard" },
  { value: "iron-kingdom-flame", label: "Iron Kingdom Flame" },
  // --- New 20 board themes (custom artwork, placeholder URLs for now) ---
  { value: "iron-kingdom",      label: "Iron Kingdom", renderMode: "full-board",
    fullBoardImage:
      "https://customer-assets.emergentagent.com/job_crown-conquest-9/artifacts/88dgfilg_1000002888.jpg" },
  { value: "dragonborn",        label: "Dragonborn", renderMode: "full-board",
    fullBoardImage:
      "https://customer-assets.emergentagent.com/job_crown-conquest-9/artifacts/so50wjg9_pexels-photo-36677596.jpg" },
  { value: "elf-court",         label: "Elf Court", renderMode: "full-board",
    fullBoardImage:
      "https://customer-assets-gfyr7b9c.emergentagent.net/job_crown-conquest-9/artifacts/aw6ced58_1000004808.png" },
  { value: "celestial-host",    label: "Celestial Host", renderMode: "full-board",
    fullBoardImage:
      "https://customer-assets.emergentagent.com/job_crown-conquest-9/artifacts/s6dyi6mv_stormy_clouds3b.jpg" },
  { value: "grass",             label: "Grass", renderMode: "full-board",
    fullBoardImage:
      "https://customer-assets.emergentagent.com/job_crown-conquest-9/artifacts/qfuigpzk_1000003844.png" },
  { value: "gothic",            label: "Gothic", renderMode: "full-board",
    fullBoardImage:
      "https://customer-assets.emergentagent.com/job_crown-conquest-9/artifacts/uc70atyr_1000003848.png" },
  { value: "stained-glass",     label: "Stained Glass", renderMode: "full-board",
    fullBoardImage:
      "https://customer-assets.emergentagent.com/job_crown-conquest-9/artifacts/1vegdrhk_pexels-photo-4562650.jpg" },
  { value: "broken-glass",      label: "Broken Glass", renderMode: "full-board",
    fullBoardImage:
      "https://customer-assets.emergentagent.com/job_crown-conquest-9/artifacts/mfjyo9fb_wickedmarble8s.jpg" },
  { value: "samurai-courtyard", label: "Samurai Courtyard", renderMode: "full-board",
    fullBoardImage:
      "https://customer-assets.emergentagent.com/job_crown-conquest-9/artifacts/k1fyj0hz_1000003843.png" },
  { value: "yin-yang-dragon",   label: "Yin-Yang Dragon", renderMode: "full-board",
    fullBoardImage:
      "https://customer-assets.emergentagent.com/job_crown-conquest-9/artifacts/o2qt4xdu_yin-yang-dragon.png" },
  { value: "eagle",             label: "Eagle", renderMode: "full-board",
    fullBoardImage:
      "https://customer-assets-gfyr7b9c.emergentagent.net/job_crown-conquest-9/artifacts/c1v18bhp_1000004810.png" },
  { value: "tiger",             label: "Tiger", renderMode: "full-board",
    fullBoardImage:
      "https://customer-assets-gfyr7b9c.emergentagent.net/job_crown-conquest-9/artifacts/w4a6w9qn_1000004816.png" },
  { value: "lion",              label: "Lion", renderMode: "full-board",
    fullBoardImage:
      "https://customer-assets-gfyr7b9c.emergentagent.net/job_crown-conquest-9/artifacts/7293kmge_1000004811.png" },
  { value: "cobra",             label: "Cobra", renderMode: "full-board",
    fullBoardImage:
      "https://customer-assets-gfyr7b9c.emergentagent.net/job_crown-conquest-9/artifacts/3913fi0x_1000004821.png" },
  { value: "dragon",            label: "Dragon", renderMode: "full-board",
    fullBoardImage:
      "https://customer-assets-gfyr7b9c.emergentagent.net/job_crown-conquest-9/artifacts/sln6kzw3_1000004815.png" },
  { value: "wolf-bear",         label: "Wolf & Bear", renderMode: "full-board",
    fullBoardImage:
      "https://customer-assets-gfyr7b9c.emergentagent.net/job_crown-conquest-9/artifacts/tyzu18n6_1000004823.png" },
  { value: "monkey-tiger",      label: "Monkey & Tiger", renderMode: "full-board",
    fullBoardImage:
      "https://customer-assets-gfyr7b9c.emergentagent.net/job_crown-conquest-9/artifacts/jqshijzx_1000004822.png" },
  { value: "dragons-pair",      label: "Dragons (Pair)", renderMode: "full-board",
    fullBoardImage:
      "https://customer-assets.emergentagent.com/job_crown-conquest-9/artifacts/ewlka5j2_dragons-pair.png" },
  { value: "old-glory",         label: "Old Glory", renderMode: "full-board",
    fullBoardImage:
      "https://customer-assets.emergentagent.com/job_crown-conquest-9/artifacts/ww8g6goc_old-glory.png" },
  { value: "gladiator-arena",   label: "Gladiator Arena", renderMode: "full-board",
    fullBoardImage:
      "https://customer-assets.emergentagent.com/job_crown-conquest-9/artifacts/l3t5udjo_gladiator-arena.png" },
];

/** All valid render mode values for [data-board-render-mode]. */
export const RENDER_MODES = ["per-cell", "full-board"];
const DEFAULT_RENDER_MODE = "per-cell";

/**
 * Resolve the render mode for a given board style value. Falls back to the
 * legacy "per-cell" mode when the style has no explicit `renderMode` field,
 * so any style added in the past continues rendering identically.
 */
export function getBoardRenderMode(value) {
  const entry = BOARD_STYLES.find((s) => s.value === value);
  const mode = entry?.renderMode;
  return RENDER_MODES.includes(mode) ? mode : DEFAULT_RENDER_MODE;
}

/**
 * Full-board image URL for a given style (or `null` for styles that do not
 * opt into full-board mode). Kept as a plain lookup so App.css can also
 * declare its own `--board-full-image` block in parallel — either path
 * works, and this JS-side lookup is what the provider writes onto <html>.
 */
export function getFullBoardImage(value) {
  const entry = BOARD_STYLES.find((s) => s.value === value);
  return entry?.fullBoardImage || null;
}

/* ---------------------------------------------------------------------------
 * FPC wing-region color system (Feb 2026):
 *
 * When Four Player Chess renders in full-board mode, the central 8×8 arena
 * shows the style's `--board-full-image`; the four surrounding wings paint
 * a solid team/faction color instead. Which four colors are used depends on
 * which board style is active:
 *
 *   • Faction-badged styles (the 8 listed in FACTION_BADGED_STYLES) —
 *     Iron Kingdom / Dragonborn / Elf Court / Celestial Host and their
 *     "-flame" variants — pull each seat's wing color from
 *     factionThemes.FACTION_THEMES[<faction>].tokenColors.gilt via the
 *     SEAT_TO_FACTION seat→faction convention. This makes wings echo the
 *     seat's pledged faction identity.
 *
 *   • Every other style (neutral / animal / heraldic art / etc.) uses the
 *     UNIFIED_SEAT_COLORS constants — the same four values as the base
 *     FPC selection-border palette in App.css and PieceSvg's FACTION map,
 *     so a user's wing/border/piece-tint all speak with one voice.
 *
 * factionThemes.js is READ ONLY here — no colors are modified in that
 * file. This module only imports SEAT_TO_FACTION + FACTION_THEMES and
 * writes derived CSS custom properties onto <html>.
 * ------------------------------------------------------------------------- */
export const FACTION_BADGED_STYLES = new Set([
  "iron-kingdom",
  "dragonborn",
  "elf-court",
  "celestial-host",
  "iron-kingdom-flame",
  "dragonborn-flame",
  "elf-court-flame",
  "celestial-host-flame",
]);

export const UNIFIED_SEAT_COLORS = {
  south: "#D9A441", // golden orange
  west:  "#4550C4", // indigo blue
  north: "#E2374F", // crimson red
  east:  "#39FF14", // neon green
};

/* ---------------------------------------------------------------------------
 * Board Layout override (Feb 2026):
 *
 * A style's `renderMode` field defines its DEFAULT layout (per-cell tile vs
 * full-board image). The user can flip that default per-style via a single
 * "Board Layout" toggle in Settings. The override is a single global
 * "tiled" | "full-board" | null value stored in localStorage — null means
 * "use the style's own default", which is what ships out of the box.
 *
 * The four procedural base styles (wood / marble / green-felt / blue-stone)
 * have no source image, so full-board mode is not meaningful for them.
 * `styleSupportsFullBoard()` is the single canonical guard: it returns
 * true only if the style has a resolvable `fullBoardImage`. Any code that
 * needs to decide whether the toggle is meaningful should call this — the
 * effect below, the Settings UI, and any future consumer.
 * ------------------------------------------------------------------------- */
export const RENDER_OVERRIDE_KEY = "chess_board_render_override";

/** True iff the style has an image to render as one continuous board. */
export function styleSupportsFullBoard(value) {
  return Boolean(getFullBoardImage(value));
}

/**
 * Resolve the EFFECTIVE render mode: the override wins when set AND the
 * style supports it, otherwise the style's own default applies. Procedural
 * base styles can only render per-cell regardless of override.
 */
export function resolveRenderMode(value, override) {
  if (override && RENDER_MODES.includes(override)) {
    if (override === "full-board" && !styleSupportsFullBoard(value)) {
      // Guarded: procedural bases (or any style without an image) can't
      // legally be full-board. Fall through to the style's own default.
      return getBoardRenderMode(value);
    }
    return override;
  }
  return getBoardRenderMode(value);
}

/** Resolve the four wing colors for the currently-active board style. */
export function getWingColors(value) {
  if (FACTION_BADGED_STYLES.has(value)) {
    // Faction-badged: pull each seat's gilt color from factionThemes.
    // Faction-badged "-flame" variants intentionally share the same
    // faction identity (e.g. iron-kingdom-flame → iron-kingdom), so we
    // reduce to the core faction key via strip-"-flame".
    const coreFaction = value.replace(/-flame$/, "");
    return Object.fromEntries(
      Object.entries(SEAT_TO_FACTION).map(([seat, seatFaction]) => {
        // The active board style may itself declare a faction that
        // takes priority for its own seat — but the wing-color legend
        // is a per-seat mapping, so we always read the seat's own
        // faction color regardless of which style is active.
        void coreFaction;
        return [seat, FACTION_THEMES[seatFaction].tokenColors.gilt];
      })
    );
  }
  return { ...UNIFIED_SEAT_COLORS };
}

/* ---------------------------------------------------------------------------
 * Player-facing board adjustment sliders (Feb 2026):
 *   • Brightness   — 0.5 … 1.5, default 1.0  (neutral)
 *   • Tint         — { color: "#XXXXXX", alpha: 0 … 1 }, default alpha 0
 *   • Checker      — 0 … 1 strength, default 0 (per-cell) or 1 (full-board)
 *
 * These write CSS custom properties on <html> so both `.cell::before` (per-cell
 * mode) and `.board-grid::before` / `.board-grid::after` (full-board mode)
 * pick them up via `var(--…, default)` lookups in App.css. Nothing changes
 * visually until the user actively moves a slider — every fallback in App.css
 * is chosen so that "unset" == "neutral".
 * ------------------------------------------------------------------------- */
const BRIGHTNESS_DEFAULT = 1;
const TINT_DEFAULT = { color: "#000000", alpha: 0 };
const STORAGE_BRIGHTNESS = "chess_board_brightness";
const STORAGE_TINT = "chess_board_tint";
const STORAGE_CHECKER = "chess_board_checker";
// Feb 2026 additions — universal wing tint intensity + full-board image scope.
// Both persist alongside the existing sliders and default to values that
// reproduce the pre-existing rendering pixel-for-pixel:
//   • Wing tint 0     = image visible on wings (no tint applied)
//   • Image  "whole"  = original single-image-across-whole-board behaviour
const STORAGE_WING_TINT = "chess_board_wing_tint";
const STORAGE_IMAGE_SCOPE = "chess_board_image_scope";
const WING_TINT_DEFAULT = 0;
const IMAGE_SCOPE_DEFAULT = "whole";  // "whole" | "arena"

/** Convert `#RRGGBB` + alpha 0..1 → `rgba(r,g,b,a)` for CSS. */
function tintToRgba({ color, alpha }) {
  const hex = (color || "#000000").replace("#", "");
  const r = parseInt(hex.slice(0, 2), 16) || 0;
  const g = parseInt(hex.slice(2, 4), 16) || 0;
  const b = parseInt(hex.slice(4, 6), 16) || 0;
  const a = Math.max(0, Math.min(1, Number(alpha) || 0));
  return `rgba(${r}, ${g}, ${b}, ${a})`;
}

const DEFAULT_STYLE = "wood";
const STORAGE_KEY = "chess_board_style";

const Ctx = createContext({
  boardStyle: DEFAULT_STYLE,
  setBoardStyle: () => {},
  brightness: BRIGHTNESS_DEFAULT,
  setBrightness: () => {},
  tint: TINT_DEFAULT,
  setTint: () => {},
  checker: 0,
  setChecker: () => {},
  wingTintIntensity: 0,
  setWingTintIntensity: () => {},
  imageScope: "whole",
  setImageScope: () => {},
});

function safeParse(raw, fallback) {
  try {
    if (raw == null) return fallback;
    const v = JSON.parse(raw);
    return v == null ? fallback : v;
  } catch {
    return fallback;
  }
}

export function BoardStyleProvider({ children }) {
  const [boardStyle, setBoardStyleState] = useState(() => {
    if (typeof window === "undefined") return DEFAULT_STYLE;
    const saved = window.localStorage.getItem(STORAGE_KEY);
    return BOARD_STYLES.some((s) => s.value === saved) ? saved : DEFAULT_STYLE;
  });

  const [brightness, setBrightness] = useState(() => {
    if (typeof window === "undefined") return BRIGHTNESS_DEFAULT;
    const raw = window.localStorage.getItem(STORAGE_BRIGHTNESS);
    const n = Number(raw);
    return Number.isFinite(n) && n >= 0.5 && n <= 1.5 ? n : BRIGHTNESS_DEFAULT;
  });

  const [tint, setTint] = useState(() => {
    if (typeof window === "undefined") return TINT_DEFAULT;
    return safeParse(window.localStorage.getItem(STORAGE_TINT), TINT_DEFAULT);
  });

  // `checker` is a 0..1 strength multiplier that scales the checker-mask
  // alpha. Default is deliberately 0 — the CSS `var(--…, fallback)` chain
  // takes over at 0 so every mode's built-in fallback (transparent for
  // per-cell, 0.32/0.06 for full-board) is what ships. Once the slider
  // moves, both modes render an explicit `rgba(0,0,0,α)` / white overlay.
  const [checker, setChecker] = useState(() => {
    if (typeof window === "undefined") return 0;
    const raw = window.localStorage.getItem(STORAGE_CHECKER);
    const n = Number(raw);
    return Number.isFinite(n) && n >= 0 && n <= 1 ? n : 0;
  });

  // Universal wing-tint intensity (Feb 2026). Applies on EVERY board
  // style, not just the original 3 (neon-whiteboard / inkboard /
  // faction_wars route). Color is always the fixed seat/team/faction
  // color from `getWingColors(boardStyle)` — this slider only scales
  // its alpha, so intensity is user-controlled but the identity is
  // system-controlled (no free color picker).
  const [wingTintIntensity, setWingTintIntensity] = useState(() => {
    if (typeof window === "undefined") return WING_TINT_DEFAULT;
    const raw = window.localStorage.getItem(STORAGE_WING_TINT);
    const n = Number(raw);
    return Number.isFinite(n) && n >= 0 && n <= 1 ? n : WING_TINT_DEFAULT;
  });

  // Full-board image scope (Feb 2026):
  //   "whole" — image spans the entire board (legacy behaviour)
  //   "arena" — image is restricted to the central 8×8 arena; the four
  //             wings render at full-opacity tinted color, producing a
  //             more dramatic focal-point look. Implementation trick:
  //             the wings' opaque backgrounds naturally occlude the
  //             ::before image underneath, so no separate mask/clip is
  //             needed. `getEffectiveWingAlpha` (below) forces 1 when
  //             the scope is "arena", overriding the slider.
  const [imageScope, setImageScope] = useState(() => {
    if (typeof window === "undefined") return IMAGE_SCOPE_DEFAULT;
    const raw = window.localStorage.getItem(STORAGE_IMAGE_SCOPE);
    return raw === "arena" || raw === "whole" ? raw : IMAGE_SCOPE_DEFAULT;
  });

  // Board Layout override: "tiled" | "full-board" | null. Null = follow the
  // style's own `renderMode` default. Same load/persist pattern as `checker`.
  const [renderModeOverride, setRenderModeOverride] = useState(() => {
    if (typeof window === "undefined") return null;
    const raw = window.localStorage.getItem(RENDER_OVERRIDE_KEY);
    if (raw === "tiled") return "per-cell";
    if (raw === "full-board" || raw === "per-cell") return raw;
    return null;
  });

  // ---------------------------------------------------------------------
  // Route awareness (Feb 2026) — needed so the wing-region color tint can
  // be scoped to Faction Wars specifically. BoardStyleProvider mounts
  // OUTSIDE <BrowserRouter> (see App.js), so React Router hooks are not
  // available here. We fall back to `window.location.pathname` and keep
  // it reactive by subscribing to `popstate` plus a synthetic
  // "bsc:locationchange" event that we dispatch after monkey-patching
  // history.pushState / replaceState (a widely-used shim so client-side
  // navigations trigger listeners the same way browser back/forward
  // already does). Idempotent via a window-level flag.
  // ---------------------------------------------------------------------
  const [pathname, setPathname] = useState(() =>
    typeof window !== "undefined" ? window.location.pathname : "",
  );
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const update = () => setPathname(window.location.pathname);
    if (!window.__bscHistoryPatched) {
      const origPush = window.history.pushState;
      const origReplace = window.history.replaceState;
      window.history.pushState = function patchedPush(...args) {
        const ret = origPush.apply(this, args);
        window.dispatchEvent(new Event("bsc:locationchange"));
        return ret;
      };
      window.history.replaceState = function patchedReplace(...args) {
        const ret = origReplace.apply(this, args);
        window.dispatchEvent(new Event("bsc:locationchange"));
        return ret;
      };
      window.__bscHistoryPatched = true;
    }
    window.addEventListener("popstate", update);
    window.addEventListener("bsc:locationchange", update);
    return () => {
      window.removeEventListener("popstate", update);
      window.removeEventListener("bsc:locationchange", update);
    };
  }, []);
  const isFactionWarsRoute = pathname.startsWith("/faction-wars/");

  // Apply the style + its render mode as attributes on <html> so the CSS
  // variables and mode-scoped rules flow to every board (lobby preview,
  // game screen, puzzle screen, replay viewer). `data-board-render-mode`
  // is additive — its absence is treated by our CSS as identical to the
  // legacy "per-cell" behaviour, so removing it is a safe no-op.
  useEffect(() => {
    if (typeof document !== "undefined") {
      const html = document.documentElement;
      html.setAttribute("data-board-style", boardStyle);
      html.setAttribute(
        "data-board-render-mode",
        resolveRenderMode(boardStyle, renderModeOverride),
      );
      // Full-board image URL, if the style opted in. Setting to empty
      // string via removeProperty when not applicable so any per-cell
      // style following a full-board one doesn't inherit a stale URL.
      const fbUrl = getFullBoardImage(boardStyle);
      if (fbUrl) {
        html.style.setProperty("--board-full-image", `url("${fbUrl}")`);
      } else {
        html.style.removeProperty("--board-full-image");
      }
      // Wing colors — Feb 2026 (universal): the seat/team/faction fixed
      // color set is written for EVERY board style, on every route.
      // Whether the tint is visible is driven purely by the alpha of the
      // `--wing-tint-alpha` custom property, which the slider below
      // controls. This replaces the earlier "on for 3 styles / off
      // otherwise" gate — the color identity is still system-driven
      // (no free color picker), only its intensity is user-driven.
      const wings = getWingColors(boardStyle);
      html.style.setProperty("--wing-south-color", wings.south);
      html.style.setProperty("--wing-west-color",  wings.west);
      html.style.setProperty("--wing-north-color", wings.north);
      html.style.setProperty("--wing-east-color",  wings.east);
      // Image scope attribute — CSS keys arena-only rules off this.
      // "whole" (default) keeps the pre-existing pixel-for-pixel look.
      html.setAttribute("data-board-image-scope", imageScope);
      // Preserve the Faction Wars route hint for any legacy CSS that
      // still branches on it; harmless when unused.
      if (isFactionWarsRoute) {
        html.setAttribute("data-fw-route", "1");
      } else {
        html.removeAttribute("data-fw-route");
      }
    }
    if (typeof window !== "undefined") {
      window.localStorage.setItem(STORAGE_KEY, boardStyle);
      // Persist the render-mode override alongside the style itself so
      // both react to changes on the same effect. `tiled` is written in
      // preference to `per-cell` for a friendlier localStorage value.
      if (renderModeOverride === null) {
        window.localStorage.removeItem(RENDER_OVERRIDE_KEY);
      } else if (renderModeOverride === "per-cell") {
        window.localStorage.setItem(RENDER_OVERRIDE_KEY, "tiled");
      } else {
        window.localStorage.setItem(RENDER_OVERRIDE_KEY, renderModeOverride);
      }
    }
  }, [boardStyle, renderModeOverride, isFactionWarsRoute]);

  // Push slider values to <html> as CSS custom properties. Every value at
  // its neutral default resolves to "unset" in CSS (empty string via
  // removeProperty), so App.css `var(--…, fallback)` uses each rule's
  // built-in neutral fallback — which is why non-Iron-Kingdom styles look
  // pixel-identical to before this change.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const html = document.documentElement;
    // Brightness
    if (brightness === BRIGHTNESS_DEFAULT) {
      html.style.removeProperty("--board-brightness");
    } else {
      html.style.setProperty("--board-brightness", String(brightness));
    }
    // Tint
    if (!tint || !tint.alpha) {
      html.style.removeProperty("--board-tint-color");
    } else {
      html.style.setProperty("--board-tint-color", tintToRgba(tint));
    }
    // Checker
    if (!checker) {
      html.style.removeProperty("--board-mask-color");
      html.style.removeProperty("--board-mask-light");
    } else {
      // Symmetric mask: dark squares get black at α, light squares get
      // white at 0.25× that α (a soft wash on the light side keeps the
      // checker legible without overexposing the artwork).
      html.style.setProperty(
        "--board-mask-color",
        `rgba(0, 0, 0, ${(checker * 0.5).toFixed(3)})`,
      );
      html.style.setProperty(
        "--board-mask-light",
        `rgba(255, 255, 255, ${(checker * 0.12).toFixed(3)})`,
      );
    }
    // Wing tint alpha (Feb 2026). Effective value forces 1.0 in
    // "arena" image-scope mode so wings become fully opaque and thus
    // occlude the full-board ::before image outside the arena — that's
    // how arena-only rendering is achieved without extra CSS clips or
    // masks. In "whole" mode, the slider value is honoured directly.
    const effectiveWingAlpha =
      imageScope === "arena" ? 1 : Math.max(0, Math.min(1, Number(wingTintIntensity) || 0));
    if (effectiveWingAlpha === 0) {
      html.style.removeProperty("--wing-tint-alpha");
    } else {
      html.style.setProperty("--wing-tint-alpha", String(effectiveWingAlpha));
    }
    // Persist
    try {
      window.localStorage.setItem(STORAGE_BRIGHTNESS, String(brightness));
      window.localStorage.setItem(STORAGE_TINT, JSON.stringify(tint));
      window.localStorage.setItem(STORAGE_CHECKER, String(checker));
      window.localStorage.setItem(STORAGE_WING_TINT, String(wingTintIntensity));
      window.localStorage.setItem(STORAGE_IMAGE_SCOPE, imageScope);
    } catch { /* localStorage unavailable — non-fatal */ }
  }, [brightness, tint, checker, wingTintIntensity, imageScope]);

  return (
    <Ctx.Provider
      value={{
        boardStyle,
        setBoardStyle: setBoardStyleState,
        brightness,
        setBrightness,
        tint,
        setTint,
        checker,
        setChecker,
        wingTintIntensity,
        setWingTintIntensity,
        imageScope,
        setImageScope,
        renderModeOverride,
        setRenderModeOverride,
        // Convenience helpers for consumers (e.g. Settings UI) so they
        // don't have to import the module-level helpers separately.
        currentStyleSupportsFullBoard: styleSupportsFullBoard(boardStyle),
        effectiveRenderMode: resolveRenderMode(boardStyle, renderModeOverride),
      }}
    >
      {children}
    </Ctx.Provider>
  );
}

export function useBoardStyle() {
  return useContext(Ctx);
}
